import React, { useState, useEffect, useRef } from "react";
import { Loader, PrefillFileLabel } from "../../../styledComponents/Components.styles";
import { Modal, Fade, Box, Tooltip } from "@mui/material";
import palette from "../../../../utilities/palette";
import { validateSRPdGenericFormat } from "../../../../utilities/importFormatValidation";
import { useDataContext } from "../../../DataProvider/DataContext";
import { handleImportSRPd } from "../../../../utilities/processImportSrpd";
import { CommonButton } from "../../../styledComponents/MUI.styles";
import FormatErrorDisplayBlock, { buildFormatErrorDisplay, FormatErrorDisplay } from "./FormatErrorDisplay";


/**
 * This component is a file uploader that allows the user to upload a single file.
 */
const ImportSection = ({ showModal, setShowModal, setError}) => {
  const [file, setFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [formatErrorDisplay, setFormatErrorDisplay] = useState<FormatErrorDisplay | null>(null);
  const { apiService, setImportProcessData, ecuCataSelectObj, setECUCataSelectObj } = useDataContext();
  const [isImportErrorCaught, setIsImportErrorCaught] = useState(false);
  const [isImportProcessing, setIsImportProcessing] = useState(false);  
  const fileInputRef = useRef(null);  


useEffect(() => {
    setFile(null);
    setErrorMessage("");
  setFormatErrorDisplay(null);
    setIsImportErrorCaught(false);
    setIsImportProcessing(false);
},[showModal]);

  const validateFileFormat = async (selectedFile) => {
    try {
      if (!(selectedFile.type === 'application/json' && selectedFile.name.toLowerCase().endsWith('.json'))) {
        setFormatErrorDisplay(null);
        setErrorMessage('File must be a JSON file');
        return false;
      }

      // return false if the file content is testData
      let data = null;
      try {
        data = JSON.parse(await selectedFile.text());
      } catch (e) {
        console.log(`Error reading file: ${e}`);
        setFormatErrorDisplay(null);
        setErrorMessage("Data is not in JSON format. Please upload a valid JSON file.");
        return false;
      }

      const validationResult = validateSRPdGenericFormat(data);
      if (!validationResult.isValid) {
        const nextFormatErrorDisplay = buildFormatErrorDisplay(validationResult.errors);
        setFormatErrorDisplay(nextFormatErrorDisplay);
        setErrorMessage(nextFormatErrorDisplay.displayedErrors[0] ?? "Format validation failed.");
        return false;
      }

      setFormatErrorDisplay(null);
      setErrorMessage("");
      console.log("data", data);
      return true;
    } catch (e) {
      console.log(`Error validating file: ${e}`);
    }
  }

  // Function to handle the file change event
  const handleFileChange = async (e) => {
    try {
      e.preventDefault();
      setErrorMessage("");
      setFormatErrorDisplay(null);
      console.log("handleFileChange triggered");
      const files = e.target.files ?? e.dataTransfer.files;

      if (files) {
        const addedFile = files[0];
        setFile(addedFile);
        await validateFileFormat(addedFile);
      }
    } catch (e) {
      console.log(`Error handling file change: ${e}`);
      setErrorMessage("An unexpected error occurred while selecting the file. If problem persists, please contact the development team for support.");
    }
  };

  return (
      <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={showModal}
      onClose={() => {
        setShowModal(false);
      }}
      closeAfterTransition
      disableEnforceFocus
      disableAutoFocus
  >
      <Fade in={showModal}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 600,
            backgroundColor: 'white',
            border: "1px solid #ccc",
          borderRadius: "5px",
            }}>
      
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          padding: "10px",
          width: "100%",          
        }}
      >
        <div style={{ fontSize: "20px", marginBottom: "10px", display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
          <div style={{ color: palette.main }}>IMPORT SRPD</div>
          <Tooltip title="Close Import Section">
            <button
              type="button"
              style={{
                fontSize:'20px', 
                fontWeight:'bold', 
                cursor:'pointer',
                background: 'none',
                border: 'none',
                padding: 0,
                color: 'inherit',
                position: 'absolute',
                right: 0
              }}
              onClick={() => {
                setShowModal(false);
                setFile(null);
                setErrorMessage("");
                setFormatErrorDisplay(null);
              }}
              aria-label="Close Prefill Section">
              x
            </button>
          </Tooltip>
        </div>
            <div style={{marginLeft: '20px'}}>
              <input
                type="file"
                id="prefillFileInput"
                accept=".json"
                onClick={(event) => {
                  event.currentTarget.value = "";
                }}
                onChange={handleFileChange}
                style={{display: 'none'}}
                ref={fileInputRef}
              />
              <PrefillFileLabel
                htmlFor="prefillFileInput"
                >
                Click to select a JSON file to import SRPd
                </PrefillFileLabel>

            </div>
                <div className="fileDisplay">
                  <ul style={{display:'flex', flexDirection: 'column', gap: '5px'}}>
                    <li>Name: {file?.name ?? "No file selected"}</li>
                    <li>Type: {file?.type ?? "Unknown"}</li>
                    <li>Size: {file?.size ?? 0} bytes</li>
                  </ul>
                  {formatErrorDisplay ? (
                    <FormatErrorDisplayBlock formatErrorDisplay={formatErrorDisplay} />
                  ) : (
                    errorMessage && <div style={{color: palette.stepper.error, marginLeft: '20px', marginBottom: '10px'}}> {errorMessage} </div>
                  )}
                  {isImportErrorCaught && <div style={{color: palette.stepper.error, marginLeft: '20px', marginBottom: '10px'}}> An unexpected error occurred while processing the imported file. If problem perists, please contact cmeyer75 or vluong5 for support.</div>}
                </div>

                <CommonButton
                  sx={{width: '150px', height: '37px', alignSelf: 'center'}}
                  disabled={!file || errorMessage !== ""}
                  onClick={(e) => {

                      e.preventDefault();
                      (async () => {
                        try {
                        setError({
                          isError: false,
                          message: ""
                        })
                        setErrorMessage("");
                        setFormatErrorDisplay(null);
                        setIsImportErrorCaught(false);
                        setIsImportProcessing(true);
                        await handleImportSRPd({
                          data: JSON.parse(await file.text()),
                          setImportProcessData,
                          setShowModal,
                          apiService,
                          setIsImportErrorCaught,
                          ecuCataSelectObj,
                          setECUCataSelectObj
                        })
                        setIsImportProcessing(false);
                      } catch (e) {
                        console.log("Unexpected error during import:", e);
                        setFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                        setIsImportProcessing(false);
                        if (e instanceof Error && e.message.startsWith("The requested file could not be read, typically due to permission problems that have occurred after a reference to a file was acquired.")) {
                          setFormatErrorDisplay(null);
                          setErrorMessage("File modified or moved. Please re-select the file and try again.");
                          return;
                        } 
                        setIsImportErrorCaught(true);
                        
                    }
                  })();

                  }}
                >
                  {isImportProcessing ? <Loader/> : "Import SRPd"}
                </CommonButton>
      </div>
          </Box>
      </Fade>
  </Modal>

  );
};

export default ImportSection;